/**
 * Tutorial Step Definitions
 * 
 * Dynamically generates tutorial steps based on:
 * - Header buttons (left to right)
 * - Sidebar menu sections (top to bottom, parent items only)
 * 
 * The tutorial auto-adjusts when menu items are rearranged.
 * 
 * Features:
 * - target: CSS selector for the element to highlight
 * - expandSidebar: true to expand collapsed sidebar
 * 
 * Last Updated: December 2025
 */

import { MENU_STRUCTURE } from '../../config/menuStructure';

// Descriptions for each menu section (parent items only)
const MENU_DESCRIPTIONS = {
  'home': 'Your project home page with quick access to key information and recent activity.',
  'documentationManagement': 'Manage project documentation including transmittal logs for tracking document distribution.',
  'estimation': 'Build project estimates: MTO, Material Index, Labour Rates, Rentals, Cost Breakdown, and lookup tables.',
  'progressTrackers': 'Track work progress across all disciplines: Piping, Mechanical, Structural, HVAC, Electrical, Civil, and Change Orders.',
  'schedule': 'Manage project schedules and milestone tracking with Schedule Instances.',
  'actuals': 'Track actual spent hours vs budgeted hours. Upload timesheet data and monitor performance.',
  'materialManagement': 'Material Log, Tariff lookup (harmonized codes), Purchasing Lists, and Currency Exchange rates.',
  'changeManagement': 'Track and manage project changes with Change Log, Control Charts, and change requests.',
  'procurement': 'Manage procurement including contractor selection and vendor evaluation.',
  'technical': 'Technical documentation: Daily Reports, Sheets, Files, Specs, Issues, RFIs, Submittals, and more.',
  'qa-qc': 'Quality Assurance/Control: Checklists, inspections, weld reports, test reports, NCRs, and punch lists.',
  'safety': 'Health, Safety & Environment: JHA/JSA, Toolbox Talks, Permits, Incident Reports, Safety Certificates.',
  'training': 'Training resources: Portal tutorials, Sharepoint guides, Keyboard Shortcuts, Mouse & Trackpad tips.',
  'processes': 'Standard Operating Procedures: Requisitions, Purchasing, Estimation, Scheduling, Suggestion Box.',
  'reports': 'Generate reports: Budget, Actuals, Earned Value, EVM (CPI/SPI), Client Reports, and HSE Reports.',
  'development': 'Development tools and test features (admin/developer access).',
  'download-app': 'Download the mobile app for iOS and Android devices.'
};

export const getTutorialSteps = (location) => {
  // Determine which tutorial to show based on current location
  if (location === '/') {
    return getHomeTutorialSteps();
  } else if (location === '/projects') {
    return getProjectsListTutorialSteps();
  } else if (location.startsWith('/project/')) {
    return getProjectDetailTutorialSteps();
  }
  return getHomeTutorialSteps(); // Default to home tutorial
};

const getHomeTutorialSteps = () => [
  {
    id: 'welcome',
    title: 'Welcome to LGL Portal! 👋',
    content: 'Welcome to the LGL Portal - your central hub for project management, progress tracking, estimation, material management, and reporting. This quick tour will show you the key features.',
    target: null,
    roles: null
  },
  {
    id: 'logo',
    title: 'LGL Logo',
    content: 'Click the LGL logo anytime to return to the main landing page.',
    target: '[data-tutorial="lgl-logo"]',
    roles: null
  },
  {
    id: 'project-dropdown',
    title: 'Project Dropdown',
    content: 'Quickly switch between projects. Click to see a searchable list of all your assigned projects. Type to filter by name or job number.',
    target: '[data-tutorial="project-dropdown"]',
    roles: null
  },
  {
    id: 'home-button',
    title: 'Home Button',
    content: 'Click the Home icon to return to the main landing page from anywhere in the app.',
    target: '[data-tutorial="home-button"]',
    roles: null
  },
  {
    id: 'help-dropdown',
    title: 'Help & Resources',
    content: 'Hover over the "?" button to access: Help Center (documentation), Learning Center (shortcuts, tutorials), and Contact Us & Support.',
    target: '[data-tutorial="help-dropdown"]',
    roles: null
  },
  {
    id: 'next-steps',
    title: 'Ready to Start! 🚀',
    content: 'Use the Project Dropdown to select a project and start working. Each project has a full sidebar menu with all tools organized by category.',
    target: '[data-tutorial="project-dropdown"]',
    roles: null
  }
];

const getProjectsListTutorialSteps = () => [
  {
    id: 'projects-overview',
    title: 'Your Projects Dashboard',
    content: 'This is your projects dashboard showing all projects you have access to. Each card displays the job number, project name, client, and active disciplines.',
    target: '[data-tutorial="projects-list"]',
    roles: null
  },
  {
    id: 'search-bar',
    title: 'Search Projects',
    content: 'Use the search bar to quickly find projects by name or job number. Results filter in real-time as you type.',
    target: '[data-tutorial="search-bar"]',
    roles: null
  },
  {
    id: 'project-card',
    title: 'Project Cards',
    content: 'Each card shows project info at a glance. Colored badges indicate active disciplines. Click any card to open the project.',
    target: '[data-tutorial="project-card"]',
    roles: null
  },
  {
    id: 'create-project',
    title: 'Create New Project',
    content: 'Project Managers can create new projects using the "+ New" button. Set the job number, name, client, and which disciplines to enable.',
    target: '[data-tutorial="create-project"]',
    roles: ['pm', 'admin', 'superadmin']
  },
  {
    id: 'complete',
    title: 'You\'re Ready! 🎉',
    content: 'Click any project card to open it and explore all its features. The sidebar menu will show all available tools.',
    target: '[data-tutorial="project-card"]',
    roles: null
  }
];

/**
 * Dynamically generates project detail tutorial steps
 * based on the current MENU_STRUCTURE configuration.
 * 
 * Order: Header buttons (left to right) → Sidebar sections (top to bottom)
 */
const getProjectDetailTutorialSteps = () => {
  const steps = [];

  // 1. Welcome step
  steps.push({
    id: 'project-welcome',
    title: 'Your Project Workspace',
    content: 'This is your project workspace. Let\'s start with the header navigation, then explore the sidebar menu.',
    target: null,
    roles: null
  });

  // 2. Header buttons (left to right)
  steps.push({
    id: 'header-logo',
    title: 'LGL Logo',
    content: 'Click the LGL logo to return to the main landing page from anywhere.',
    target: '[data-tutorial="lgl-logo"]',
    roles: null
  });

  steps.push({
    id: 'header-project-dropdown',
    title: 'Project Dropdown',
    content: 'Quickly switch between projects without going back to the projects list. Click to see all your projects with search.',
    target: '[data-tutorial="project-dropdown"]',
    roles: null
  });

  steps.push({
    id: 'header-home',
    title: 'Home Button',
    content: 'Click the Home icon to return to the main landing page.',
    target: '[data-tutorial="home-button"]',
    roles: null
  });

  steps.push({
    id: 'header-help',
    title: 'Help & Support',
    content: 'Hover here to access Help Center, Learning Center (keyboard shortcuts, tutorials), and Contact Support.',
    target: '[data-tutorial="help-dropdown"]',
    roles: null
  });

  // 3. Sidebar introduction
  steps.push({
    id: 'sidebar-intro',
    title: 'Project Sidebar',
    content: 'The sidebar contains all project tools organized by category. Let\'s go through each section from top to bottom.',
    target: '[data-tutorial="project-sidebar"]',
    expandSidebar: true,
    roles: null
  });

  // 4. Dynamically add steps for each parent menu item (top to bottom)
  // Filter to only include sections that have descriptions (visible main sections)
  const parentSections = MENU_STRUCTURE.filter(section => {
    // Skip items without descriptions (not main navigation items)
    return MENU_DESCRIPTIONS[section.id];
  });

  parentSections.forEach((section, index) => {
    const description = MENU_DESCRIPTIONS[section.id] || `Access ${section.label} features and tools.`;
    
    steps.push({
      id: `sidebar-${section.id}`,
      title: `${section.icon} ${section.label}`,
      content: description,
      target: `[data-tutorial="${section.id}-menu"]`,
      expandSidebar: true,
      roles: section.id === 'development' ? ['admin', 'superadmin'] : null
    });
  });

  // 5. Keyboard tips
  steps.push({
    id: 'keyboard-tips',
    title: 'Keyboard Tips ⌨️',
    content: 'Quick shortcuts: Tab moves to next cell, Enter saves and moves down, Escape cancels edits, Ctrl+F searches. In trackers, you can drag-fill values like Excel!',
    target: null,
    roles: null
  });

  // 6. Tour complete
  steps.push({
    id: 'tour-complete',
    title: 'You\'re All Set! 🎉',
    content: 'Explore the sidebar to discover all features. Restart this tour anytime from the Help dropdown. Happy working!',
    target: null,
    roles: null
  });

  return steps;
};

// Export all steps for reference
export const ALL_TUTORIAL_STEPS = {
  home: getHomeTutorialSteps(),
  projectsList: getProjectsListTutorialSteps(),
  projectDetail: getProjectDetailTutorialSteps()
};
